import { computed, getCurrentInstance } from 'vue';
import { NotificationModel } from '../../../shared/models/notification.model';
import { notificationEventTypes } from '../../../shared/enums/notificationEventTypes';
import { dateFormatEnum } from '../../../shared/enums/dateFormatEnum';
import truncate from 'lodash/truncate';
import { RepeatingPattern } from '../../business/calendar/repeatingPattern';
import { DateTimeUtil } from '../../../shared/utils/dateTimeUtil';
import i18n from '../../../shared/locales/i18n';

/**
 * Composable for generating notification content based on notification types
 */
export function useNotificationContent(notification: NotificationModel) {
  const instance = getCurrentInstance();
  if (!instance) {
    throw new Error('useNotificationContent must be called within setup()');
  }

  const getText = (key: string, params?: Record<string, any>): string => instance.proxy.$getText(key, params) as string;
  const $t = (key: string, params?: Record<string, any>): string => instance.proxy.$t(key, params) as string;

  const type = computed((): string => {
    switch (notification.notificationEventType) {
      case notificationEventTypes.EVENT_CANCELLED_NO_REASON:
      case notificationEventTypes.SINGLE_OCCURRENCE_EVENT_CANCELLED_NO_REASON:
      case notificationEventTypes.EVENT_CANCELLED_BY_SOMEONE_ELSE:
        return `${getText('ALERT_CANCELLED')}:`;
      case notificationEventTypes.ADDED_TO_COMMON_INBOX:
        return getText('ALERT_COMMON_INBOX_SHARED');
      case notificationEventTypes.REMOVED_FROM_COMMON_INBOX:
        return getText('ALERT_REMOVED_FROM_COMMON_INBOX');
      case notificationEventTypes.REMOVED_RIGHTS_FOR_OTHERS_CALENDAR:
        return getText('ALERT_RIGHTS_LOST_1');
      case notificationEventTypes.GRANTED_RIGHTS_FOR_OTHER_CALENDAR:
        return getText('ALERT_RIGHTS_GRANTED_1');
      case notificationEventTypes.VACATION_REQUEST_CANCELLED_NO_REASON:
        return getText('ALERT_VACATION_REQUEST');
      case notificationEventTypes.POST_WAS_DELETED:
      case notificationEventTypes.POST_WAS_REMOVED_FROM_GROUP_BY_ADMIN:
        return getText('ALERT_TYPE_POST_WAS_DELETED');
      default:
        return '';
    }
  });

  const separator = computed((): string => {
    switch (notification.notificationEventType) {
      case notificationEventTypes.EVENT_CHANGED_RESPONSE_REQUIRED:
      case notificationEventTypes.INVITED_TO_EVENT_RESPONSE_REQUIRED:
      case notificationEventTypes.INVITED_TO_REPEATING_EVENT:
      case notificationEventTypes.INVITED_TO_PERFORMANCE_MEETING:
      case notificationEventTypes.INVITED_TO_PARENTAL_MEETING:
      case notificationEventTypes.INVITED_TO_SCHOOL_HOME_MEETING:
      case notificationEventTypes.VACATION_RESPONSE_REQUIRED:
      case notificationEventTypes.INVITED_TO_EXCEPTION_EVENT:
      case notificationEventTypes.INVITED_TO_SINGLE_OCCURRENCE_OF_EVENT:
      case notificationEventTypes.INVITED_TO_SURVEY:
        return ' -';
      case notificationEventTypes.GENERAL_INFORMATION_SENT:
        return '';
      default:
        return ':';
    }
  });

  const title = computed((): string => {
    if (!notification.title) {
      return '';
    }

    switch (notification.notificationEventType) {
      case notificationEventTypes.EVENT_CHANGED_BY_SOMEONE_ELSE:
      case notificationEventTypes.RESOURCE_RESERVATION_DECLINED:
        return '';
      case notificationEventTypes.GENERAL_INFORMATION_SENT:
        return `${truncate(notification.title, { length: 80 })} ${separator.value}`;
      default:
        return `${truncate(notification.title)} ${separator.value}`;
    }
  });

  const getSurveyContent = (): string => {
    const prefix = getText('ALERT_EXPIRES');
    const expiryDate = DateTimeUtil.formatDate(notification.expires, dateFormatEnum.SHORT_DATE);
    return `${prefix} ${expiryDate}`;
  };

  const getCancelledEventContent = (): string => {
    const triggeredTime = DateTimeUtil.formatDate(notification.triggered, dateFormatEnum.LONG_DATE_TIME);
    const action = getText('ALERT_IS_CANCELLED');
    let startTime = '';
    let executor = '';

    if (DateTimeUtil.isValidDate(notification.startTime)) {
      startTime = DateTimeUtil.formatDate(notification.startTime, dateFormatEnum.LONG_DATE_TIME_WITH_DATE_PREFIX);
    }
    if (notification.cancelledBy) {
      executor = `${getText('ALERT_BY')} ${notification.cancelledBy}`;
    }

    return `${startTime} ${action} ${executor} (${triggeredTime})`.trim();
  };

  const getDeletionRuleContent = (): string => notification.message || '';

  const getRemovedRightsForOthersCalendarContent = (): string =>
    `${notification.otherCalendarPersonName || ''}${getText('ALERT_RIGHTS_LOST_2')}`;

  const getGrantedRightsForOtherCalendarContent = (): string =>
    `${notification.otherCalendarPersonName || ''}${getText('ALERT_RIGHTS_GRANTED_2')}`;

  const getPostWasRemovedFromGroupByAdminContent = (): string => `
      "${truncate(notification.postTitle || '')}"
      ${getText('ALERT_POST_WAS_REMOVED_FROM_GROUP_BY_ADMIN')}
      ${notification.groupName || ''}
      `;

  const getVacationRequestCancelledNoReasonContent = (): string => getText('ALERT_DELETED');

  const getRemovedFromCommonInboxContent = (): string => notification.commonInboxName || '';

  const getPostWasDeletedContent = (): string => `
      "${truncate(notification.postTitle || '')}"
      ${getText('ALERT_POST_WAS_DELETED')}
      `;

  const getEventChangedBySomeoneElseContent = (): string => {
    const triggered = DateTimeUtil.formatDate(notification.triggered, dateFormatEnum.SHORT_DATE);
    return getText('ALERT_EVENT_IS_EDITED', {
      title: notification.title || '',
      triggered,
      updatedBy: notification.updatedBy || '',
    });
  };

  const getDefaultEventNotificationContent = (): string => {
    const { isAllDayEvent, startTime, originalEventEndTime, endTime } = notification;

    let startDate: string;
    let formattedEndTime: string;

    if (isAllDayEvent) {
      startDate = DateTimeUtil.formatUtcDate(startTime, dateFormatEnum.LONG_DATE_TIME);
      formattedEndTime = getText('CALENDAR_LABEL_ALL_DAY_END_TIME');
    } else {
      startDate = DateTimeUtil.formatDate(startTime, dateFormatEnum.LONG_DATE_TIME);
      formattedEndTime = originalEventEndTime
        ? DateTimeUtil.formatDate(originalEventEndTime, dateFormatEnum.SHORT_TIME)
        : DateTimeUtil.formatDate(endTime, dateFormatEnum.SHORT_TIME);
    }

    if (!endTime) {
      return getText('ALERT_DEFAULT_NOTIFICATION_CONTENT_WITHOUT_END_DATE', { startDate });
    }

    const endDate = DateTimeUtil.formatDate(endTime, dateFormatEnum.SHORT_DATE);

    return getText('ALERT_DEFAULT_NOTIFICATION_CONTENT_WITH_END_TIME', {
      startDate,
      endDate,
      endTime: formattedEndTime,
    });
  };

  const getInvitedToRepeatingEventContent = (): string => {
    const { isAllDayEvent, startTime, originalEventEndTime, endTime } = notification;

    const startDateTime = isAllDayEvent
      ? DateTimeUtil.formatUtcDate(startTime, dateFormatEnum.LONG_DATE_TIME)
      : DateTimeUtil.formatDate(startTime, dateFormatEnum.LONG_DATE_TIME);

    const formattedEndTime = isAllDayEvent
      ? getText('CALENDAR_LABEL_ALL_DAY_END_TIME')
      : DateTimeUtil.formatDate(originalEventEndTime ?? endTime, dateFormatEnum.SHORT_TIME);

    return getText('ALERT_REPEATED_EVENT_NOTIFICATION_CONTENT', {
      startDateTime,
      endTime: formattedEndTime,
      repeatingPattern: getRepeatingEventPattern(),
    });
  };

  const getRepeatingEventPattern = (): string =>
    RepeatingPattern.getPattern(
      notification.repeatingType,
      getRepeatingEventLastOccurrenceDate(),
      notification.weekdayMask,
      notification.dayInMonth,
      notification.dailyInterval
    );

  const getRepeatingEventLastOccurrenceDate = (): string => (notification.endTime ? getRepeatingEventEndDate() : '');

  const getRepeatingEventEndDate = (): string => {
    const { isAllDayEvent, endTime } = notification;
    return $t('calendar.end_at', {
      endDate: isAllDayEvent
        ? DateTimeUtil.formatUtcDate(endTime, dateFormatEnum.SHORT_DATE_WITHOUT_LEADING_ZERO)
        : DateTimeUtil.formatDate(endTime, dateFormatEnum.SHORT_DATE_WITHOUT_LEADING_ZERO),
    });
  };

  const getResourceReservationDeclinedContent = (): string => {
    const textReservationDeclined = getText('ALERT_RESOURCE_RESERVATION_DECLINED');
    const dateTime = DateTimeUtil.formatDate(notification.startTime, 'dddd [d.] D. MMM[.] YYYY [kl.] LT');
    return `${textReservationDeclined}: ${notification.resourceName}. <b>'${notification.title}'</b> ${dateTime}`;
  };

  const getFailedFileScanContent = () => i18n.t('file.file_was_rejected').toString();

  const content = computed((): string => {
    switch (notification.notificationEventType) {
      case notificationEventTypes.ADDED_TO_COMMON_INBOX:
        return notification.commonInboxName || '';
      case notificationEventTypes.INVITED_TO_SURVEY:
        return getSurveyContent();
      case notificationEventTypes.EVENT_CANCELLED_NO_REASON:
      case notificationEventTypes.SINGLE_OCCURRENCE_EVENT_CANCELLED_NO_REASON:
      case notificationEventTypes.EVENT_CANCELLED_BY_SOMEONE_ELSE:
        return getCancelledEventContent();
      case notificationEventTypes.DELETION_RULES_INFO:
        return getDeletionRuleContent();
      case notificationEventTypes.VACATION_REQUEST_CANCELLED_NO_REASON:
        return getVacationRequestCancelledNoReasonContent();
      case notificationEventTypes.REMOVED_FROM_COMMON_INBOX:
        return getRemovedFromCommonInboxContent();
      case notificationEventTypes.REMOVED_RIGHTS_FOR_OTHERS_CALENDAR:
        return getRemovedRightsForOthersCalendarContent();
      case notificationEventTypes.GRANTED_RIGHTS_FOR_OTHER_CALENDAR:
        return getGrantedRightsForOtherCalendarContent();
      case notificationEventTypes.POST_WAS_DELETED:
        return getPostWasDeletedContent();
      case notificationEventTypes.POST_WAS_REMOVED_FROM_GROUP_BY_ADMIN:
        return getPostWasRemovedFromGroupByAdminContent();
      case notificationEventTypes.EVENT_CHANGED_BY_SOMEONE_ELSE:
        return getEventChangedBySomeoneElseContent();
      case notificationEventTypes.EVENT_CHANGED_RESPONSE_REQUIRED:
      case notificationEventTypes.INVITED_TO_EVENT_RESPONSE_REQUIRED:
        return getDefaultEventNotificationContent();
      case notificationEventTypes.INVITED_TO_REPEATING_EVENT:
        return getInvitedToRepeatingEventContent();
      case notificationEventTypes.RESOURCE_RESERVATION_DECLINED:
        return getResourceReservationDeclinedContent();
      case notificationEventTypes.FILE_SCAN_FAILED_POST:
      case notificationEventTypes.FILE_SCAN_FAILED_EVENT:
      case notificationEventTypes.FILE_SCAN_FAILED_EVENT_OCCURRENCE_EXCEPTION:
      case notificationEventTypes.FILE_SCAN_FAILED_COMMON_FILE:
      case notificationEventTypes.FILE_SCAN_FAILED_COMMON_INBOX_MESSAGE:
      case notificationEventTypes.FILE_SCAN_FAILED_ALBUM:
      case notificationEventTypes.FILE_SCAN_FAILED_EXTERNAL_SECURE_FILE:
      case notificationEventTypes.FILE_SCAN_FAILED_INTERNAL_SECURE_DOCUMENT:
      case notificationEventTypes.FILE_SCAN_FAILED_PRIVATE_INBOX_MESSAGE:
      case notificationEventTypes.FILE_SCAN_FAILED_OTP_INBOX_MESSAGE:
      case notificationEventTypes.FILE_SCAN_FAILED_PROFILE_PICTURE:
        return getFailedFileScanContent();
      default:
        return '';
    }
  });

  const link = computed(() => {
    switch (notification.notificationEventType) {
      case notificationEventTypes.FILE_SCAN_FAILED_POST:
      case notificationEventTypes.FILE_SCAN_FAILED_EVENT:
      case notificationEventTypes.FILE_SCAN_FAILED_EVENT_OCCURRENCE_EXCEPTION:
      case notificationEventTypes.FILE_SCAN_FAILED_COMMON_FILE:
      case notificationEventTypes.FILE_SCAN_FAILED_COMMON_INBOX_MESSAGE:
      case notificationEventTypes.FILE_SCAN_FAILED_ALBUM:
      case notificationEventTypes.FILE_SCAN_FAILED_EXTERNAL_SECURE_FILE:
      case notificationEventTypes.FILE_SCAN_FAILED_INTERNAL_SECURE_DOCUMENT:
      case notificationEventTypes.FILE_SCAN_FAILED_PRIVATE_INBOX_MESSAGE:
      case notificationEventTypes.FILE_SCAN_FAILED_OTP_INBOX_MESSAGE:
      case notificationEventTypes.FILE_SCAN_FAILED_PROFILE_PICTURE:
        return i18n.t('file.go_to_file').toString();
      default:
        return '';
    }
  });

  const moreInformation = computed((): string => {
    if (
      notification.notificationEventType === notificationEventTypes.INVITED_TO_SURVEY ||
      notification.notificationEventType === notificationEventTypes.GENERAL_INFORMATION_SENT
    ) {
      return getText('ALERT_SEE_MORE');
    }
    if (notification.notificationEventType === notificationEventTypes.DELETION_RULES_INFO) {
      return getText('ALERT_READ_MORE');
    }
    if (notification.notificationEventType === notificationEventTypes.RESOURCE_RESERVATION_DECLINED) {
      return getText('ALERT_GO_TO_EVENT');
    }
    return '';
  });

  return {
    type,
    title,
    content,
    link,
    moreInformation,
    separator,
  };
}
